import { Button, Divider, Grid, H, P } from '@farewill/ui'

import { FAREWILL_LEGAL_SERVICES } from 'utils/enums'
import { ProbateCase } from 'lib/models/probate-case'
import { RESOURCES, useFetchArrayResource } from 'api'
import { Invoice } from 'lib/models/invoice'
import { COLOR } from '@farewill/ui/tokens'
import { useContactContext } from 'routes/contact/context'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { BRAND_ID } from 'config/brands'
import { findCaseBookedInvoice } from 'utils/helpers'
import MessageBox from 'components/message-box'
import apiRequest from 'lib/axios/api-request'

const SendInviteButton = styled(Button.Primary)`
  &:disabled {
    background-color: ${COLOR.GREY.LIGHT};
  }
`

const CreateProbateCustomerAccount = ({
  probateCase,
}: {
  probateCase: ProbateCase
}) => {
  const { contact, fetchContact } = useContactContext()
  const { data, isLoading } = useFetchArrayResource<Invoice>(
    {
      resource: RESOURCES.INVOICES,
      params: `filter[probateCaseId]=${probateCase.id}`,
    },
    { enabled: !!probateCase.id }
  )
  const invoices = data?.data || []
  const [creatingAccount, setCreatingAccount] = useState(false)
  const HAS_LINKED_ACCOUNT = contact?.attributes?.accountUuid
  const HAS_FINISHED_SIGN_UP = contact?.attributes?.account?.cognitoSubId

  useEffect(() => {
    fetchContact()
  }, [probateCase.attributes.contact.id, fetchContact])

  if (contact?.attributes?.brandId !== BRAND_ID.dignity) {
    return null
  }
  //Dignity Legal Services is referred to as farewill_legal_services in the database
  if (probateCase.attributes.referredTo !== FAREWILL_LEGAL_SERVICES) {
    return null
  }
  if (isLoading || !findCaseBookedInvoice(invoices)) {
    return null
  }

  const createAccont = async () => {
    setCreatingAccount(true)
    apiRequest({
      url: `/api/probate-cases/${probateCase.id}/create-account`,
      method: 'POST',
    })
  }

  return (
    <>
      <Grid.Item>
        <H size="S">Customer account</H>
        {!HAS_LINKED_ACCOUNT && (
          <>
            <P color={COLOR.GREY.DARK}>
              This customer does not have an account.
            </P>
            <MessageBox margin={[0, 0, 'S', 0]} stretch>
              Invitation emails are automatically signed by the case owner.
              Check the assigned case manager before sending.
            </MessageBox>
            <SendInviteButton
              type="button"
              onClick={() => createAccont()}
              disabled={creatingAccount}
            >
              Send account invitation email
            </SendInviteButton>
          </>
        )}
        {HAS_LINKED_ACCOUNT && !HAS_FINISHED_SIGN_UP && (
          <>
            <P color={COLOR.GREY.DARK}>
              We’ve emailed the customer with the next steps to create their
              account.
            </P>
            <Button.Primary
              type="button"
              onClick={() => createAccont()}
              disabled={creatingAccount}
            >
              Resend account invitation
            </Button.Primary>
          </>
        )}
        {HAS_LINKED_ACCOUNT && HAS_FINISHED_SIGN_UP && (
          <>
            <P color={COLOR.GREY.DARK}>This customer has an account created.</P>
            <Link to={`/contacts/${contact.id}/account`}>
              View customer account
            </Link>
          </>
        )}
      </Grid.Item>
      <Grid.Item margin={['XS', 0]}>
        <Divider />
      </Grid.Item>
    </>
  )
}

export default CreateProbateCustomerAccount
